import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api/sdk';
import { useAuth } from '@/auth/auth0-provider';
import {
  buildListQuery,
  DEFAULT_PER_PAGE,
  type Paginated,
  type PaginationParams,
} from '@/api/pagination';

export interface SiteEvent {
  id: string;
  eventType: string;
  outcome: 'success' | 'failure' | 'pending';
  actorType: 'user' | 'system' | 'integration';
  actorLabel: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface RawSiteEvent {
  id: string;
  event_type: string;
  outcome: 'success' | 'failure' | 'pending';
  actor_type: 'user' | 'system' | 'integration';
  actor_label: string;
  metadata: Record<string, unknown> | string | null;
  created_at: string;
}

const normalize = (r: RawSiteEvent): SiteEvent => ({
  id: r.id,
  eventType: r.event_type,
  outcome: r.outcome,
  actorType: r.actor_type,
  actorLabel: r.actor_label,
  metadata:
    typeof r.metadata === 'string'
      ? (JSON.parse(r.metadata) as Record<string, unknown>)
      : r.metadata,
  createdAt: r.created_at,
});

export interface UseSiteEventsOptions extends PaginationParams {
  /** SQL-level filter so we don't round-trip rows we'll discard. */
  actorType?: 'user' | 'system' | 'integration';
}

/**
 * Numbered-page audit-events stream for a site. Returns the standard
 * `Paginated<SiteEvent>` envelope used by every list endpoint.
 */
export function useSiteEvents(
  siteId: string | undefined,
  options: UseSiteEventsOptions = {},
) {
  const { isAuthenticated, isSdkReady } = useAuth();
  const { actorType, page = 1, perPage = DEFAULT_PER_PAGE } = options;

  return useQuery<Paginated<SiteEvent>>({
    queryKey: ['sites', siteId, 'events', { page, perPage, actorType }],
    queryFn: async () => {
      const url = `/api/sites/${encodeURIComponent(siteId as string)}/activity${buildListQuery(
        { page, perPage },
        { actorType },
      )}`;
      const result = await api.get<Paginated<RawSiteEvent>>(url);
      return {
        items: result.items.map(normalize),
        pagination: result.pagination,
      };
    },
    enabled: !!siteId && isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
  });
}
